import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Sub-header definitions for each shift
const metricHeaders = [
  { key: "layupStartSerialNo", label: "Layup Start Serial No" },
  { key: "layupEndSerialNo", label: "Layup End Serial No" },
  { key: "layupTotalQty", label: "Layup Total Qty" },
  { key: "quantity", label: "Packed Quantity" },
  { key: "mwp", label: "MWp" },
  { key: "cumulativeMwp", label: "Cumulative MWp" },
];

const DailyVolumeTable = ({ data = [], startDate, endDate, shifts = [], subForm }) => {
  // Static headers
  const staticHeaders = [
    { id: "line", label: "Line" },
    { id: "wattage", label: "Wattage" },
  ];

  // Get all dates strictly in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to data, or default
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["Morning", "Afternoon", "Night"];
  }, [shifts, data]);

  // Hierarchical header tree: Date -> Shifts -> 6 Metrics
  const headerStructure = useMemo(() => {
    return dates.map((date) => {
      const shiftNodes = effectiveShifts.map((shift) => {
        const key = `${date}_${shift}`;
        const metrics = metricHeaders.map((m) => ({
          id: `${m.key}_${key}`,
          metricKey: m.key,
          label: m.label,
          date,
          shift,
        }));

        return {
          shift,
          key,
          colSpan: metricHeaders.length,
          metrics,
        };
      });

      return {
        date,
        colSpan: effectiveShifts.length * metricHeaders.length,
        shifts: shiftNodes,
      };
    });
  }, [dates, effectiveShifts]);

  // Flattened leaf columns for the table body
  const flatColumns = useMemo(() => {
    return headerStructure.flatMap((d) => d.shifts.flatMap((s) => s.metrics));
  }, [headerStructure]);

  // Calculate column widths
  const columnWidths = useMemo(() => {
    return {
      line: 120,
      wattage: 120,
      value: 140,
    };
  }, []);

  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.line;
  };

  // Group table rows by Line and Wattage
  const tableRows = useMemo(() => {
    const rowMap = new Map();

    data.forEach((item) => {
      const dateKey = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!dateKey || !shift) return;

      (item.form_data?.lines || []).forEach((lineObj) => {
        const lineName = lineObj.line || "Line 1";

        Object.entries(lineObj.packedQuantity || {}).forEach(([wattage, pq]) => {
          const rowKey = `${lineName}_${wattage}`;
          if (!rowMap.has(rowKey)) {
            rowMap.set(rowKey, {
              line: lineName,
              wattage,
              data: {},
            });
          }

          const row = rowMap.get(rowKey);
          row.data[`${dateKey}_${shift}`] = {
            layupStartSerialNo: lineObj.layup?.startSerialNo ?? "N/A",
            layupEndSerialNo: lineObj.layup?.endSerialNo ?? "N/A",
            layupTotalQty: lineObj.layup?.totalQty ?? "N/A",
            quantity: pq.quantity ?? "N/A",
            mwp: pq.MWp ?? "N/A",
            cumulativeMwp: pq.cumulativeMWp ?? "N/A",
          };
        });
      });
    });

    return Array.from(rowMap.values()).sort((a, b) => {
      const lineA = String(a.line ?? "");
      const lineB = String(b.line ?? "");
      const wattageA = String(a.wattage ?? "");
      const wattageB = String(b.wattage ?? "");

      if (lineA === lineB) {
        return wattageA.localeCompare(wattageB);
      }
      return lineA.localeCompare(lineB);
    });
  }, [data]);

  const colsPerShift = metricHeaders.length;
  const colsPerDate = effectiveShifts.length * colsPerShift;

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Line", "Wattage"];
    const row2 = ["", ""];
    const row3 = ["", ""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 2, c: 0 } });
    merges.push({ s: { r: 0, c: 1 }, e: { r: 2, c: 1 } });

    let colIdx = 2;

    headerStructure.forEach((dateNode) => {
      row1.push(dateNode.date);
      for (let i = 1; i < colsPerDate; i++) row1.push("");
      if (colsPerDate > 1) {
        merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + colsPerDate - 1 } });
      }

      dateNode.shifts.forEach((shiftNode) => {
        row2.push(`Shift ${shiftNode.shift}`);
        for (let i = 1; i < colsPerShift; i++) row2.push("");
        if (colsPerShift > 1) {
          merges.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + colsPerShift - 1 } });
        }

        shiftNode.metrics.forEach((m) => {
          row3.push(m.label);
          colIdx += 1;
        });
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.line, row.wattage];
      flatColumns.forEach((col) => {
        const dateShiftData = row.data[`${col.date}_${col.shift}`] || {};
        const val = dateShiftData[col.metricKey];
        r.push(val !== undefined && val !== "" ? val : "N/A");
      });
      return r;
    });

    const sheetData = [row1, row2, row3, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 16 },
      { wch: 16 },
      ...Array(flatColumns.length).fill({ wch: 18 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Daily Volume");

    const fileName = generateExcelFileName("Daily_Volume", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array", bookSST: false });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Daily Volume of Qualified Modules
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dates.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Line, Wattage + Dates (merged across shifts and metrics) */}
            <TableRow>
              <StickyTableCell
                rowSpan={3}
                columnWidth={columnWidths.line}
                left={getLeftPosition(0)}
                isLastSticky={false}
              >
                Line
              </StickyTableCell>
              <StickyTableCell
                rowSpan={3}
                columnWidth={columnWidths.wattage}
                left={getLeftPosition(1)}
                isLastSticky={true}
              >
                Wattage
              </StickyTableCell>
              {headerStructure.map((dateNode) => (
                <CustomTableCell
                  key={dateNode.date}
                  colSpan={dateNode.colSpan}
                  columnWidth={columnWidths.value * dateNode.colSpan}
                >
                  {dateNode.date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shifts (sub-columns under Date) */}
            <TableRow>
              {headerStructure.flatMap((dateNode) =>
                dateNode.shifts.map((shiftNode) => (
                  <CustomTableCell
                    key={`${dateNode.date}_${shiftNode.shift}`}
                    colSpan={shiftNode.colSpan}
                    columnWidth={columnWidths.value * shiftNode.colSpan}
                  >
                    Shift {shiftNode.shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>

            {/* Row 3: Metrics (sub-columns under Shift) */}
            <TableRow>
              {headerStructure.flatMap((dateNode) =>
                dateNode.shifts.flatMap((shiftNode) =>
                  shiftNode.metrics.map((m) => (
                    <CustomTableCell
                      key={m.id}
                      columnWidth={columnWidths.value}
                    >
                      {m.label}
                    </CustomTableCell>
                  ))
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={2}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.line + columnWidths.wattage}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No dates selected
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={2 + flatColumns.length}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.line + columnWidths.wattage}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={`${row.line}_${row.wattage}_${index}`} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.line}
                    left={getLeftPosition(0)}
                    isLastSticky={false}
                  >
                    {row.line}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.wattage}
                    left={getLeftPosition(1)}
                    isLastSticky={true}
                  >
                    {row.wattage}
                  </StickyBodyTableCell>

                  {flatColumns.map((col) => {
                    const dateShiftData = row.data[`${col.date}_${col.shift}`] || {};
                    const val = dateShiftData[col.metricKey];
                    return (
                      <BodyTableCell key={`${col.id}_${index}`} columnWidth={columnWidths.value}>
                        {val !== undefined && val !== "" ? String(val) : "N/A"}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default DailyVolumeTable;